/*
 * Purpose: Auth flow primitives — card, inputs, buttons, method tabs, email context
 * Last modified: 2026-07-10 | Agent
 * Completeness: 98
 */
import { type InputHTMLAttributes, type ReactNode } from 'react';
import { Check, Fingerprint, Hash, Mail, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AuthSignInMethod = 'magic_link' | 'email_otp' | 'passkey';

export type AuthEmailContextVariant = 'ready' | 'magic_sent' | 'otp_sent' | 'passkey';

const METHOD_ICONS: Record<AuthSignInMethod, typeof Sparkles> = {
  magic_link: Sparkles,
  email_otp: Hash,
  passkey: Fingerprint,
};

const METHOD_HINTS: Record<AuthSignInMethod, string> = {
  magic_link: 'One-click link — no password.',
  email_otp: '6-digit code from your inbox.',
  passkey: 'Face ID, Touch ID, or security key.',
};

export function isAuthEmailValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function AuthMethodTabs({
  methods,
  value,
  onChange,
}: {
  methods: { id: AuthSignInMethod; label: string }[];
  value: AuthSignInMethod;
  onChange: (id: AuthSignInMethod) => void;
}) {
  return (
    <div className="space-y-2">
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${methods.length}, minmax(0, 1fr))` }}
        role="tablist"
        aria-label="Sign-in method"
      >
        {methods.map((m) => {
          const selected = value === m.id;
          const Icon = METHOD_ICONS[m.id];
          return (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(m.id)}
              className={cn(
                'relative flex min-h-[4.25rem] flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2.5 text-center transition-all',
                selected
                  ? 'border-[var(--auth-accent)] bg-[var(--auth-accent)]/15 text-[var(--auth-fg)] shadow-sm ring-2 ring-[var(--auth-accent)]/45'
                  : 'border-[var(--routines-color-border-default)] text-[var(--auth-muted)] hover:border-[var(--auth-accent)]/35 hover:bg-[var(--auth-accent)]/5 hover:text-[var(--auth-fg)]'
              )}
            >
              {selected ? (
                <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--auth-accent)] text-[var(--auth-accent-fg,white)]">
                  <Check className="h-2.5 w-2.5" aria-hidden />
                </span>
              ) : null}
              <Icon className={cn('h-4 w-4 shrink-0', selected ? 'text-[var(--auth-accent)]' : '')} aria-hidden />
              <span className={cn('text-[11px] leading-tight sm:text-xs', selected ? 'font-semibold' : 'font-medium')}>
                {m.label}
              </span>
              {selected ? (
                <span className="text-[9px] font-medium uppercase tracking-wide text-[var(--auth-accent)]">Selected</span>
              ) : null}
            </button>
          );
        })}
      </div>
      <p className="text-center text-xs text-[var(--auth-muted)]">{METHOD_HINTS[value]}</p>
    </div>
  );
}

export function AuthEmailContextBanner({
  email,
  variant,
  onChangeEmail,
}: {
  email: string;
  variant: AuthEmailContextVariant;
  onChangeEmail?: () => void;
}) {
  const trimmed = email.trim();
  if (!trimmed) return null;

  const copy =
    variant === 'magic_sent'
      ? 'Magic link sent to'
      : variant === 'otp_sent'
        ? '6-digit code sent to'
        : variant === 'passkey'
          ? 'Passkey sign-in for'
          : 'Signing in as';

  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-[var(--auth-accent)]/35 bg-[var(--auth-accent)]/10 px-3 py-2.5"
      role="status"
      aria-live="polite"
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--auth-accent)]/20 text-[var(--auth-accent)]">
        <Mail className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--auth-muted)]">{copy}</p>
        <p className="truncate text-sm font-semibold text-[var(--auth-fg)]" title={trimmed}>
          {trimmed}
        </p>
      </div>
      {onChangeEmail ? (
        <button
          type="button"
          onClick={onChangeEmail}
          className="shrink-0 text-xs font-medium text-[var(--auth-accent)] underline underline-offset-2 hover:opacity-90"
        >
          Change
        </button>
      ) : null}
    </div>
  );
}

export function AuthCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'w-full max-w-md space-y-4 rounded-xl border border-[var(--auth-border)] bg-[var(--auth-surface)] p-6 sm:p-8 shadow-lg',
        className
      )}
    >
      {children}
    </div>
  );
}

export function AuthButton({
  className = '',
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'min-h-11 w-full rounded-lg bg-[var(--routines-color-accent-default,var(--auth-accent))] px-4 text-sm font-medium text-[var(--routines-color-surface-default,var(--auth-accent-fg))] transition-opacity disabled:opacity-50',
        'shadow-sm hover:opacity-90',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--auth-accent)]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--auth-bg)]',
        'disabled:cursor-not-allowed',
        className
      )}
      {...props}
    />
  );
}

export function AuthButtonOutline({
  className = '',
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'min-h-11 w-full rounded-lg border border-[var(--auth-border)] bg-[var(--auth-surface)] px-4 text-sm font-medium',
        'text-[var(--auth-fg)] transition-colors',
        'hover:border-[var(--auth-accent)]/50 hover:bg-[var(--auth-accent)]/5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--auth-accent)]/40',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className
      )}
      {...props}
    />
  );
}

export function AuthInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'min-h-11 w-full rounded-lg border border-[var(--auth-input-border)] bg-[var(--auth-input-bg)] px-3 text-sm text-[var(--auth-fg)]',
        'placeholder:text-[var(--auth-muted)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--auth-accent)]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--auth-bg)]',
        props.className
      )}
    />
  );
}

export function AuthLabel({ children }: { children: ReactNode }) {
  return <span className="mb-1 block text-xs font-medium text-[var(--auth-muted)]">{children}</span>;
}
